import type { SelectMultipleOnSelectAll } from '@/components_deprecated'
import {
  DateRangePicker,
  MonthPicker,
  Select,
  Toggle,
} from '@/components_deprecated'
import { SelectMultipleUsers } from '@/components_deprecated/Select/SelectMultipleUsers'
import { DropdownNames } from '@/components_deprecated/Select/types'
import { UserOrderBy } from '@/modules/activities'
import { useActivityExportMutations } from '@/modules/activities/mutations.hooks'
import { responsiveExportGridAreas } from '@/modules/companies/util'
import { ExportFormat, ExportOption, ExportType } from '@/modules/exports'
import {
  useExportFormatOptions,
  useExportOptions,
} from '@/modules/exports/hooks'
import { handleDateRangeChange } from '@/utils/date/handleDateRangeChange'
import { notEmptyArrayValidation } from '@/utils/forms'
import { useCompany, useUser } from '@/utils/hooks'
import { ComponentSwitch, Icon } from '@ur-solutions/react-components'
import { useForm, useTranslate } from '@ur-solutions/react-hooks'
import { endOfMonth, formatISO, startOfMonth } from 'date-fns'
import React, { useCallback, useMemo } from 'react'
import { isMobile } from 'react-device-detect'
import styled from 'styled-components'
import type { FormButton } from '../common'
import { Field, Form, OldSection, SettingsLoader } from '../common'
import type { ExportCardProps } from '../types'
import { ExportSectionHeader, MassExportSectionHeader } from './components'

const SelectMultipleUsersWrapper = styled.div`
  position: relative;
  min-height: 54px;
`

interface ActivitiesForm {
  month: Date
  dateStart: Date
  dateEnd: Date
  exportOption: ExportOption
  exportFormat: ExportFormat
  useDateRange: boolean
  users: string[]
  weekly: boolean
}

export const ExportActivitiesForSalaryCard: React.FC<ExportCardProps> = ({
  loading,
  // TODO unused?
  onLoadingChangedCallback: _onLoadingChangedCallback,
}) => {
  const translations = useTranslate({
    exportWeekly: 'activities.export-monthly-weekly',
    activities: 'common.activities',
    exportAll: 'settings.export-all-driver-activities',
    period: 'common.period',
    exportType: 'common.type',
    exportOption: 'common.export-type',
    exportFormat: 'common.format',
    exportMethod: 'common.export-method',

    export: 'common.export',
    orderBy: 'common.order-by',
    selectUsers: 'common.select-users',
    selectAll: 'common.select-all',
    includeAbsences: 'settings.include-absences',

    lastName: 'common.lastName',
    userType: 'common.user-type',
    externalId: 'common.external-id',

    selectUsersError: 'activities.errors.select-multiple-users',
  })

  const me = useUser()
  const company = useCompany()

  const { exportActivitiesMass } = useActivityExportMutations()

  const {
    formValues: form,
    formErrors: errors,
    submitHandler: submit,
    formChangeHandler: handler,
    formValid,
    updateForm,
  } = useForm<ActivitiesForm>({
    values: {
      month: new Date(),
      dateStart: new Date(),
      dateEnd: new Date(),
      exportOption: ExportOption.SAVE,
      exportFormat: ExportFormat.PDF,
      useDateRange: false,
      users: [],
      weekly: false,
    },
    validators: {
      users: value =>
        notEmptyArrayValidation(value) ? translations.selectUsersError : null,
    },
    config: {
      initAsInvalid: true,
    },
  })

  const getDateInterval = useCallback(
    (startDate: Date, endDate?: Date): [string, string] => {
      if (form.useDateRange) {
        const dateEnd = endDate || new Date()
        return [formatISO(startDate), formatISO(dateEnd)]
      }
      return [
        formatISO(startOfMonth(startDate)),
        formatISO(endOfMonth(startDate)),
      ]
    },
    [form.useDateRange]
  )

  const handleExport = useCallback(
    (values: ActivitiesForm) => {
      const date = form.useDateRange
        ? form.dateStart
        : startOfMonth(values.month)
      const [dateStart, dateEnd] = getDateInterval(date, form.dateEnd)
      const includeAbsences = false

      void exportActivitiesMass({
        variables: {
          exportType: ExportType.ENTRY_SALARY,
          exportFormat: values.exportFormat,
          user: me.id,
          users: form.users,
          company: company.id,
          dateStart: dateStart,
          dateEnd: dateEnd,
          includeAbsences: includeAbsences,
          exportOption: values.exportOption,
          weekly: values.weekly,
          orderBy: UserOrderBy.FAMILY_NAME,
        },
      })
    },
    [
      company.id,
      exportActivitiesMass,
      form.dateEnd,
      form.dateStart,
      form.useDateRange,
      form.users,
      getDateInterval,
      me.id,
    ]
  )

  const exportOptions = useExportOptions(false)
  const excludeFormats = [ExportFormat.EXCEL_FLAT]
  const exportFormats = useExportFormatOptions(excludeFormats)

  const selectAllOnChange: SelectMultipleOnSelectAll = (
    options,
    setSelectAll
  ) => {
    return (value: boolean) => {
      if (value) {
        updateForm({ users: options.map(option => option.value) as string[] })
        setSelectAll(true)
      } else {
        updateForm({ users: [] })
        setSelectAll(false)
      }
    }
  }
  const areas = !isMobile
    ? `
        'month month  exportOption'
        'user user exportFormat'
        'includeAbsences includeAbsences includeAbsences'
      `
    : `
        ${responsiveExportGridAreas(true)}
        'includeAbsences'
      `

  const formButtons = useMemo<FormButton[]>(
    () => [
      {
        text: (
          <>
            <Icon
              icon="download"
              type="light"
              color="primary"
              margin="0 0.5ch 0 0"
            />
            {translations.export}
          </>
        ),
        type: 'submit',
        buttonProps: {
          fullWidth: isMobile,
          disabled: loading || !formValid,
        },
        onClick: submit(handleExport),
      },
    ],
    [formValid, handleExport, loading, submit, translations.export]
  )

  return (
    <>
      <ExportSectionHeader>
        {translations.activities}
        {loading && <SettingsLoader />}
      </ExportSectionHeader>
      <OldSection>
        <MassExportSectionHeader>
          {translations.exportAll}
          <Toggle
            value={form.useDateRange}
            thumbBackground="secondary"
            iconMode="svg"
            localOnIconProps={{
              icon: 'calendar-selection',
              fill: form.useDateRange ? 'white' : 'primary',
            }}
            localOffIconProps={{
              icon: 'calendar-month',
              fill: !form.useDateRange ? 'white' : 'primary',
            }}
            onChange={() => handler('useDateRange')(!form.useDateRange)}
          />
        </MassExportSectionHeader>

        <Form
          grid={{
            columns: !isMobile ? '1fr 2fr 2fr' : '1fr',
            areas: areas,
          }}
          buttons={formButtons}
        >
          <Field area="month">
            <label>{translations.period}</label>
            <ComponentSwitch>
              <ComponentSwitch.Case case={form.useDateRange}>
                <DateRangePicker
                  value={[form.dateStart, form.dateEnd]}
                  grayed={false}
                  onChange={handleDateRangeChange(updateForm)}
                />
              </ComponentSwitch.Case>

              <ComponentSwitch.Case default>
                <MonthPicker
                  value={form.month}
                  light
                  futureYears={0}
                  noYearCycle
                  onChange={handler('month')}
                />
              </ComponentSwitch.Case>
            </ComponentSwitch>
          </Field>

          <Field area="exportOption">
            <label>{translations.exportMethod}</label>
            <Select
              value={form.exportOption}
              options={exportOptions}
              fullWidth
              height="3.875rem"
              onChange={value => value && handler('exportOption')(value)}
            />
          </Field>

          <Field area="exportFormat">
            <label>{translations.exportFormat}</label>
            <Select
              value={form.exportFormat}
              options={exportFormats}
              fullWidth
              height="3.875rem"
              onChange={value => value && handler('exportFormat')(value)}
            />
          </Field>

          <Field area="user">
            <label>{translations.selectUsers}</label>
            <SelectMultipleUsersWrapper>
              <SelectMultipleUsers
                selected={form.users ?? []}
                dropdownPageName={DropdownNames.ACTIVITIES}
                grayed={false}
                error={errors.users}
                width="100%"
                minWidth={isMobile ? '0' : '260px'}
                onChange={handler('users')}
                onSelectAll={selectAllOnChange}
                withSelectAll={true}
              />
            </SelectMultipleUsersWrapper>
          </Field>

          {/* {(
            <Field area="includeAbsences">
              <label>{translations.includeAbsences}</label>
              <div>
                <Checkbox
                  checked={false}
                  onChange={() => void 0}
                  label={translations.includeAbsences}
                  inline
                  disabled
                  grayed
                />
              </div>
            </Field>
          )} */}
        </Form>
      </OldSection>
    </>
  )
}
